/**
 * HTTP reminder service — gateway `/reminders` endpoints (proxied to
 * taxhive-notification-service).
 *
 * Contract notes (verified against taxhive-notification-service reminder_routes.py):
 *  - By entity: `GET  /reminders/by-entity/{entity_type}/{entity_id}` →
 *               `{ items, total, page, page_size }`.
 *  - Create:    `POST /reminders` — body `{ title, due_date, entity_type,
 *               entity_id, reminder_type, priority, description, notify_channels }`.
 *               Tenant + creator derived from the auth token.
 *  - Update:    `PUT  /reminders/{id}` — partial field update.
 *  - Complete:  `POST /reminders/{id}/complete` `{ completion_notes }`.
 *  - Dismiss:   `POST /reminders/{id}/dismiss`  `{ dismissed_reason }`.
 *  - Snooze:    `POST /reminders/{id}/snooze`   `{ snooze_minutes }`.
 *  - Delete:    `DELETE /reminders/{id}` (soft delete, 204).
 */

import { apiClient, toServiceError } from '@/services/http/client'
import type { ReminderService } from './reminders.service'
import type {
  Reminder,
  CreateReminderInput,
  UpdateReminderInput,
  ReminderEntityType,
} from '@/types/reminder.types'
import { mapReminderFromApi, mapReminderListFromApi } from './mappers'

const DEFAULT_PAGE_SIZE = 100

export function createHttpReminderService(): ReminderService {
  return {
    async listForEntity(
      entityType: ReminderEntityType,
      entityId: string
    ): Promise<Reminder[]> {
      try {
        const res = await apiClient.get(
          `/reminders/by-entity/${entityType}/${entityId}`,
          { params: { page: 1, page_size: DEFAULT_PAGE_SIZE } }
        )
        return mapReminderListFromApi(res.data)
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async create(input: CreateReminderInput): Promise<Reminder> {
      try {
        const body: Record<string, unknown> = {
          title: input.title,
          due_date: input.dueDate,
          entity_type: input.entityType,
          entity_id: input.entityId,
        }
        if (input.description) body.description = input.description
        if (input.reminderType) body.reminder_type = input.reminderType
        if (input.priority) body.priority = input.priority
        if (input.notifyChannels) body.notify_channels = input.notifyChannels
        if (input.assignedToUserId) body.assigned_to_user_id = input.assignedToUserId
        if (input.assignedToName) body.assigned_to_name = input.assignedToName
        if (input.recurrence && input.recurrence !== 'none') {
          body.is_recurring = true
          body.recurrence_frequency = input.recurrence
        }
        if (input.remindBeforeMinutes !== undefined) body.remind_before_minutes = input.remindBeforeMinutes

        const res = await apiClient.post('/reminders', body)
        return mapReminderFromApi(res.data)
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async update(id: string, patch: UpdateReminderInput): Promise<Reminder> {
      try {
        // Status flips + snooze map to dedicated sub-action endpoints.
        if (patch.snoozeMinutes != null) {
          const res = await apiClient.post(`/reminders/${id}/snooze`, {
            snooze_minutes: patch.snoozeMinutes,
          })
          return mapReminderFromApi(res.data)
        }
        if (patch.status === 'completed') {
          const res = await apiClient.post(`/reminders/${id}/complete`, {
            completion_notes: patch.notes ?? null,
          })
          return mapReminderFromApi(res.data)
        }
        if (patch.status === 'dismissed') {
          const res = await apiClient.post(`/reminders/${id}/dismiss`, {
            dismissed_reason: patch.notes ?? null,
          })
          return mapReminderFromApi(res.data)
        }

        // Plain field update.
        const body: Record<string, unknown> = {}
        if (patch.title != null) body.title = patch.title
        if (patch.description != null) body.description = patch.description
        if (patch.priority != null) body.priority = patch.priority
        if (patch.dueDate != null) body.due_date = patch.dueDate
        if (patch.status != null) body.status = patch.status
        if (patch.notifyChannels != null) body.notify_channels = patch.notifyChannels

        const res = await apiClient.put(`/reminders/${id}`, body)
        return mapReminderFromApi(res.data)
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async remove(id: string): Promise<void> {
      try {
        await apiClient.delete(`/reminders/${id}`)
      } catch (err) {
        throw toServiceError(err)
      }
    },
  }
}
